import './init/log.js';
import log from 'loglevel';
import EventEmitter from 'event-emitter';
import BoardApi from './api/BoardApi';
import DocApi from './api/DocApi';
import VodApi from './api/VodApi';
import ZoomApi from './api/ZoomApi';
import { OperateBoardEnum, ZOOM_FACTORS, DEFAULT_WATERMARK_OPTION } from './common/Constant';
import Store from './common/Store';
import MessageController from './core/MessageController';
import SimulateMsgService from './net/SimulateMsgService';
import MyEvent from './utils/Event';
import { debug, mixin } from './utils/extends';
import Board from './view/Board';
import Document from './view/Document';
import { DocEvent } from './view/EventEnum';
import Vod from './vod/Vod';
import CommonApi from './api/CommonApi';
import { checkZoomFactors, isObject } from './utils/tool';
// 放大缩小比例值可选数据数组，所有的实例共享。jssdk的实例也可以直接统一设置，所以直接作为静态属性挂在DocModuler上
let _zoomFactors = ZOOM_FACTORS; // 初始默认；

let _watermarkImg = null;
let _watermarkOption = DEFAULT_WATERMARK_OPTION;

let _rotateDeg = 0;

let _isAPP = false;

/**
 *
 * SDK 文档模块
 * @export
 * @class DocModuler
 */
export default class DocModuler {
  constructor() {
    this.CLASS_NAME = this.constructor.name;

    EventEmitter(this);
    // 回放模块声明
    this._vod = null;
    // 当前容器
    this._currentContainer = null;

    this.store = new Store();
    this._ee = new MyEvent();
    this.store.setKV('ee', this._ee);
    this.store.setKV('scaleFactor', 1);

    mixin(this, CommonApi, this.store);
    this._inited = true;
    this.lastPageInfo = {
      slideIndex: null,
      stepIndex: null
    };
    log.info(`init complete. ver:${DocModuler.version}`);
  }

  /**
   * 获取当前文档模块版本号
   *
   * @readonly
   * @static
   * @memberof DocModuler
   */
  static get version() {
    return process.env.CORE_VERSION;
  }

  /**
   * 获取放大缩小比例值可选数据数组
   */
  static get zoomFactors() {
    return _zoomFactors;
  }

  /**
   * 设置放大缩小比例值可选数据数组
   */
  static set zoomFactors(value) {
    if (checkZoomFactors(value)) {
      _zoomFactors = value;
    }
  }
  /**
   * 获取水印配置项
   */
  static get watermarkOption() {
    return _watermarkOption;
  }

  /**
   * 设置水印配置项
   */
  static set watermarkOption(option) {
    _watermarkOption = Object.assign({}, DEFAULT_WATERMARK_OPTION, option);
  }
  /**
   * 获取水印图
   */
  static get watermarkImg() {
    return _watermarkImg;
  }

  /**
   * 设置水印图
   */
  static set watermarkImg(value) {
    _watermarkImg = value;
  }
  /**
   * 获取是否app
   */
  static get isAPP() {
    return _isAPP;
  }

  /**
   * 设置是否app
   */
  static set isAPP(value) {
    _isAPP = value;
  }

  /**
   * 创建水印
   * 在水印区域大小有变化时，可以直接调用 setWatermark(true) 重新生成
   * @param {object} option 水印选项, 不设置此项，默认会使用先前设置的 DocModuler.watermarkOption 参数
   * @param {boolean} always 每次重新生成水印图片，不缓存，默认false。
   * @returns
   */
  setWatermark(option, always = false) {
    if (typeof option === 'boolean') {
      always = option;
      option = null;
    }
    if (isObject(option)) {
      //  水印参数校验
      if (option.opacity > 1) {
        option.opacity = 1;
      }
      if (option.opacity < 0) {
        option.opacity = 0;
      }
      if (option.fontSize < 12) {
        option.fontSize = 12;
      }
      if (option.fontSize > 48) {
        option.fontSize = 48;
      }

      if (typeof option.text === 'string') {
        option.text = option.text.trim();
      } else {
        option.text = '';
      }
      DocModuler.watermarkOption = option;
    }
    if (!DocModuler.watermarkOption.text) {
      // console.warn('水印内容为空，不创建水印');
      return;
    }

    if (always) {
      // 每次重新生成水印图片，不缓存
      DocModuler.watermarkImg = null;
    }

    this.container.operateDoodle(OperateBoardEnum.SetWatermark);
  }

  clearWatermark() {
    this.container.operateDoodle(OperateBoardEnum.ClearWatermark);
  }

  /***
   * 当前文档的缩放比数值
   */
  get scaleFactor() {
    return this.store.getKV('scaleFactor');
  }

  /**
   * 创建一个文档实例
   *
   * @param {*} id
   * @param {*} uid
   * @param {*} [opts={}]
   * @returns
   * @memberof DocModuler
   */
  createDocument(id, uid, opts = {}) {
    this.destroy();

    if (opts.watermarkOption) {
      DocModuler.watermarkOption = opts.watermarkOption;
    }

    this.container = new Document(id, uid, opts);
    this._initMessageController();
    this.container.store = this.store;
    if (opts.container) {
      const c = opts.container;
      this.container.editable = c.editable;
      this.container.pageable = c.pageable;
      this.container.cacheable = false;
      this.store.setKV('editable', c.editable);
      this.store.setKV('pageable', c.pageable);
    }

    // 合并API接口道this上
    mixin(this, DocApi, this.container, this.store);
    mixin(this, BoardApi, this.container);

    mixin(this, ZoomApi, this.container, this.store);

    this._inited = true;
    return this.container;
  }

  /**
   * 创建一个白板实例
   *
   * @param {*} id
   * @param {*} uid
   * @param {*} [opts={}]
   * @returns
   * @memberof DocModuler
   */
  createBoard(id, uid, opts = {}) {
    log.debug(`createBoard. id:${id}`);
    this.destroy();

    if (opts.watermarkOption) {
      DocModuler.watermarkOption = opts.watermarkOption;
    }

    this.container = new Board(id, uid, opts);
    this._initMessageController();
    this.container.store = this.store;
    if (opts.container) {
      const c = opts.container;
      this.container.editable = c.editable;
      this.container.cacheable = false;
      this.store.setKV('editable', c.editable);
    }
    // 合并api接口到this上
    mixin(this, BoardApi, this.container);
    mixin(this, ZoomApi, this.container, this.store);

    this._inited = true;
    log.debug(`createBoard end. id:${id}`);
    return this.container;
  }

  _initMessageController() {
    if (this._callBK) {
      this.registMessager(this._callBK);
      this._callBK = null;
    }

    this.initEvents();
  }

  /**
   * 设置当前文档的数据
   *
   * @param {*} val
   * @memberof DocModuler
   */
  setData(val) {
    this.container.data = val;
  }

  /**
   * 设置当前文档的所有数据
   *
   * @param {*} val
   * @memberof DocModuler
   */
  setAllData(val) {
    this.container.setAllData(val);
  }

  /**
   * 设置容器大小
   *
   * @param {*} w
   * @param {*} h
   * @memberof DocModuler
   */
  setSize(w, h) {
    this.container.resize(w, h);

    // 容器大小变化，水印重新创建，否则可能铺不满
    this.setWatermark(true);
  }

  /**
   * 获取当前页面所有数据
   *
   * @memberof DocModuler
   */
  getPageInfo() {
    return this.container.pageInfo;
  }

  /**
   * 重设舞台容器的大小, 然后，文档和画板根据比例填充
   *
   * @param {*} w
   * @param {*} h
   * @memberof DocModuler
   */
  resize(w, h) {
    this.store.setKV('cw', w);
    this.store.setKV('ch', h);
    this.container.resize(w, h);
  }

  modifyOriginSize(w, h) {
    this.store.setKV('originW', w);
    this.store.setKV('originH', h);
    this.resize(w, h);
  }

  /**
   * 注册消息收发器，该消息收发器实现接口收、发
   *
   * @param {*} callBK
   * @memberof DocModuler
   */
  registMessager(callBK) {
    this._callBK = callBK;
    let m = new SimulateMsgService(callBK);

    if (!this.mc) {
      this.mc = new MessageController(this.store);
    }

    this.mc.registMsg(m);
  }

  /**
   * 卸载消息收发器
   *
   * @memberof DocModuler
   */
  unregistMessage() {
    this.mc.unregistMsg();
  }

  /**
   * 开启调试模式
   *
   * @memberof DocModuler
   */
  openDebug(path) {
    debug(path);
  }

  createUUID(prefix = 'document', len = 7) {
    let baseStr = new Array(len).fill('x').join('');
    var d = new Date().getTime();
    var uuid = baseStr.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

    let result = prefix !== '' ? `${prefix}-${uuid}` : uuid;
    return result;
  }

  /**
   * 销毁方法
   *
   * @memberof DocModuler
   */
  destroy() {
    if (this.container) {
      this.container.destroy();
      this._currentContainer = null;
    }

    if (this.mc) {
      this.mc.destroy();
      this.mc = null;
    }

    this._ee.removeAllListeners();
    this.eventInitComplete = false;

    if (this._vod) {
      this._vod.destroy();
      this._vod = null;
    }

    // this.store.reset()
    this._inited = false;
  }

  // ==========================================================================
  initEvents() {
    if (this.eventInitComplete) {
      return;
    }
    /**
     * 加载了一个不存在的文档，此时默认该文档加载完成，防止所有文档加载完成事件不触发
     */
    this._ee.addEventListener('docNotExit', res => {
      this.emit('documentOnload', res);
      if (!this.container.selected) {
        console.log('没有容器被选中，不派发docNotExit', res);
        return;
      }
      this.emit('docNotExit', res);
    });
    this._ee.addEventListener('documentOnload', res => {
      this.emit('documentOnload', res);
    });
    this._ee.addEventListener('doc_events', e => {
      log.info(`doc_events, ${JSON.stringify(e)}`);
      /** 页码、步数、每步时间 */
      if (e.event == DocEvent.ANIMATIONINFO) {
        this.emit('animationInfo', e);
        return;
      }
      if (!this.container.selected) {
        console.log('没有容器被选中，不派发doc_events', e);
        return;
      }
      const doc = this.container.doc;
      switch (e.event) {
        case DocEvent.PAGE_CHANGE: {
          if (!DocModuler.isAPP) {
            const {
              params: { slideIndex, stepIndex }
            } = e;
            /** 页码步数一样，无需再派发 */
            if (this.lastPageInfo.slideIndex == slideIndex && this.lastPageInfo.stepIndex == stepIndex) {
              return;
            } else {
              this.lastPageInfo.slideIndex = slideIndex;
              this.lastPageInfo.stepIndex = stepIndex;
            }
          }
          this.container.operateDoodle(OperateBoardEnum.SetCurrentId, doc.currentPageHash);

          /** 上报章节信息 */
          this.emit('slideChange', e);
          break;
        }
        case DocEvent.STEP_CHANGE: {
          if (!DocModuler.isAPP) {
            const {
              params: { slideIndex, stepIndex }
            } = e;
            /** 页码步数一样，无需再派发 */
            if (this.lastPageInfo.slideIndex == slideIndex && this.lastPageInfo.stepIndex == stepIndex) {
              return;
            } else {
              this.lastPageInfo.slideIndex = slideIndex;
              this.lastPageInfo.stepIndex = stepIndex;
            }
          }

          /** 通知移动端 */
          this.mc.sendMessageEvent('stepChange', doc.info);
          /** 上报章节信息 */
          this.emit('stepChange', e);
          break;
        }
        case DocEvent.READY:
          this.emit('documentReady', doc.info);
          // 从代码来看，ready事件不需要对外发送
          return;
        case DocEvent.PLAYBACKCOMPLETE:
          /** 播放完成事件 */
          this.emit('playbackComplete', doc.info);
          return;
        default:
          break;
      }
      /** 对外发送翻页翻步消息 */
      this.mc.sendMessage([e]);
    });

    // 日志上报
    this._ee.addEventListener('report_doc_events', e => {
      log.debug(`report_doc_events, ${JSON.stringify(e)}`);
      const doc = this.container.doc;
      /** 通知移动端，之所以把翻页放在这里是为了避免，刷新重进的用户不往外派发翻页导致移动端页码展示不对 */
      this.mc.sendMessageEvent('pageChange', { ...doc.info, cid: doc.cid });
      if (e['event'] === 'onSlideChange') {
        /**
         *
         */
        let docData = doc.info;
        this.emit('report_page_change', { doc: docData, cid: doc.cid });
      }
    });

    this._ee.addEventListener('doodle_events', o => {
      if (this.store.getKV('isVod')) {
        return;
      }
      o.event = 'doodle';
      // 对外发送画笔消息
      this.mc.sendMessage([o]);
    });

    this._ee.addEventListener('remote_message', o => {
      if (!o.hasOwnProperty('id')) {
        return;
      }

      if (!this.container.id !== o.id) {
        log.warn(`Doesn't container with id:${o.id}`);
        return;
      }

      this.container.data = o;
    });

    this._ee.addEventListener('vod_event', (res, seekDoodRes, extData) => {
      if (!this.container) {
        log.error('no container for operating or drawing.');
        return;
      }
      if (this.container.doo && this.container.doo.doo) {
        this.container.doo.doo._isVod = true;
        this.container.doo.doo._vodExtData = extData;
      }
      res.forEach(data => {
        // 如果当前容器为空，创建一个容器
        this.container.data = data;
      });
      if (extData && extData.seek) {
        // seek模式，额外处理画笔数据
        this.container.setDrawingForSeek(seekDoodRes);
      }
    });

    this._ee.addEventListener('clearDoodle', () => {
      if (this.container) {
        this.container.operateDoodle(OperateBoardEnum.Clear);
        this.container.operateDoodle(OperateBoardEnum.ClearCache);
      }
    });

    // 文档白板缩放比触发事件
    this._ee.addEventListener('scaleTriggerEvent', info => {
      // 去触发外层SDK的 scaleTrigger 事件
      this.emit('scaleTrigger', info);
    });

    this.store.setKV('ee', this._ee);
    this.eventInitComplete = true;
  }

  // ================  Getters & Setters =======================================
  get container() {
    return this._currentContainer;
  }

  set container(v) {
    this._currentContainer = v;
  }

  get containerInfos() {
    const { type, option, root, id, pageInfo, editable, cacheable } = this.container || {};
    return { type, option, root, id, pageInfo, editable, cacheable };
  }

  set editable(v) {
    this.container.editable = v;
    this.store.setKV('editable', v);
  }

  get editable() {
    return this.container.editable;
  }

  set cacheable(v) {
    this.container.cacheable = v;
  }

  get cacheable() {
    return this.container.cacheable;
  }

  set option(val) {
    if (!this.container) {
      return;
    }

    let newOpt = val;
    if (typeof val === 'string') {
      newOpt = JSON.parse(val);
    }

    // let opt = this.container.doodleOption || {};
    // Object.assign(opt, newOpt);
    this.container.operateDoodle(OperateBoardEnum.SetOption, newOpt);
  }

  get containerElement() {
    if (!this.container) {
      return null;
    }

    const { root } = this.container;
    return document.getElementById(root);
  }

  /**
   * 是否是回放模式
   *
   * @memberof DocModuler
   */
  set isVod(v) {
    if (!v) {
      return;
    }
    if (this._vod) {
      this._vod.destroy();
      this._vod = null;
    }
    this._vod = new Vod();
    // 将回放API绑定到this上
    mixin(this, VodApi, this._vod);
    this._vod.store = this.store;
    this.store.setKV('isVod', true);
    if (this.container) {
      this._vod.type = this.container.type;
      this._vod.cid = this.container.id;
      this.container.cacheable = false;
      this.container.editable = false;
    }
    this.initEvents();
  }

  get isVod() {
    return this._vod !== null;
  }

  get id() {
    if (this.container) {
      return this.container.id;
    }

    return null;
  }

  setSelected(val) {
    if (this.container) {
      this.container.setSelected(val);
    }
  }

  static get rotateDeg() {
    return _rotateDeg;
  }
  static set rotateDeg(deg) {
    _rotateDeg = deg;
  }

  /**
   * 重置undoManager的状态
   */
  resetUndoManagerData() {
    this.container.operateDoodle(OperateBoardEnum.ClearData);
    this.container.operateDoodle(OperateBoardEnum.SaveData);
  }
}
